import React, { useState, useEffect } from 'react';
import { auth } from '../../firebase/firebase-config';
import { Button, Form, Container, Row, Col, Alert, ProgressBar } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './BenefitCreate.css';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import {
  createBenefit,
  updateBenefit,
  countActivePinnedBenefits,
} from '../../services/benefitsService';
import FeaturedCarouselPinWarning, {
  useFeaturedCarouselPinWarning,
} from '../../platform/FeaturedCarouselPinWarning';
import {
  buildPinFields,
  defaultPinnedUntil,
  isPinnedActive,
  toDateInputValue,
} from '../../platform/pinnedContent';
import { useMembership } from '../../context/MembershipContext';
import { fetchUnitsForTenant } from '../../services/unitsService';
import { uploadFilesAsAttachments } from '../../services/attachmentUploadService';
import { useToast } from '../../platform/toast/ToastProvider';
import { uploadFileToStorage } from '../../services/storageUpload';
import { normalizeAttachments, syncAttachmentFields } from '../../platform/contentAttachments';
import ContentAttachmentsEditor from '../../platform/ContentAttachmentsEditor';
import { effectiveRole } from '../../platform/membershipPermissions';
import { ROLES } from '../../platform/constants';
import { useRichTextToolbarTitles } from '../../platform/richTextEditor';

const modules = {
    toolbar: [
        [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
        [{ size: [] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link'],
        ['clean']
    ],
};

function BenefitCreate({ onBenefitCreated, benefit, onBenefitUpdated }) {
    const [title, setTitle] = useState('');
    const [text, setText] = useState('');
    const [unit, setUnit] = useState('general');
    const [unitName, setUnitName] = useState('Splošno');
    const [units, setUnits] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [newFiles, setNewFiles] = useState([]);
    const [thumbnail, setThumbnail] = useState(null); // New: thumbnail state
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isPinned, setIsPinned] = useState(false);
    const [pinnedUntilDate, setPinnedUntilDate] = useState(toDateInputValue(defaultPinnedUntil()));
    const { user } = useUser();
    const { tenantId } = useTenant();
    const { memberships } = useMembership();
    const toast = useToast();
    const isEditing = !!benefit;
    const quillContainerRef = useRichTextToolbarTitles();
    const { showOverflowWarning } = useFeaturedCarouselPinWarning({
        tenantId,
        isPinned,
        excludeId: isEditing ? benefit?.id : null,
        countActivePinned: countActivePinnedBenefits,
    });

    useEffect(() => {
        if (!user || !tenantId) return;

        const loadUnits = async () => {
            const unitsList = await fetchUnitsForTenant(tenantId, { user, memberships });
            setUnits(unitsList);

            if (effectiveRole(memberships, tenantId, user) === ROLES.LOCAL_ADMIN) {
                const userUnit = unitsList.find((u) => u.id === user.unit);
                if (userUnit) {
                    setUnit(user.unit);
                    setUnitName(userUnit.unitName);
                }
            }
        };

        loadUnits();
    }, [user, tenantId, memberships]);

    useEffect(() => {
        if (isEditing && benefit) {
            setTitle(benefit.title);
            setText(benefit.text);
            setUnit(benefit.unit || 'general');
            setUnitName(benefit.unitName || 'Splošno');
            setExistingAttachments(normalizeAttachments(benefit));
            setNewFiles([]);
            const benefitPinned = isPinnedActive(benefit);
            setIsPinned(benefitPinned);
            setPinnedUntilDate(
                benefitPinned
                    ? toDateInputValue(benefit.pinnedUntil)
                    : toDateInputValue(defaultPinnedUntil()),
            );
        }
    }, [isEditing, benefit]);

    const handleUnitChange = (e) => {
        const selectedUnitId = e.target.value;
        const selectedUnit = units.find(u => u.id === selectedUnitId);
        setUnit(selectedUnitId);
        setUnitName(selectedUnit ? selectedUnit.unitName : 'Splošno');
    };

    const handleThumbnailChange = (e) => {
        const selectedImage = e.target.files[0];
        if (selectedImage && !selectedImage.type.startsWith('image/')) {
            setError('Za ikono so dovoljene samo slikovne datoteke.');
            setThumbnail(null);
        } else {
            setError('');
            setThumbnail(selectedImage);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!auth.currentUser) {
            setError('Za ustvarjanje ali urejanje ugodnosti morate biti prijavljeni.');
            return;
        }
        if (!title.trim() || !text.trim()) {
            setError('Naslov in besedilo ne smeta biti prazna.');
            return;
        }

        setUploading(true);

        try {
            const uploadedAttachments = newFiles.length
                ? await uploadFilesAsAttachments(newFiles, 'benefits', {
                    onProgress: setUploadProgress,
                    tenantId,
                })
                : [];

            let thumbnailUrl = benefit?.thumbnailUrl || '';
            if (thumbnail) {
                thumbnailUrl = await uploadFileToStorage(thumbnail, 'benefit-thumbnails', {
                    onProgress: setUploadProgress,
                    tenantId,
                });
            }

            await saveBenefit([...existingAttachments, ...uploadedAttachments], thumbnailUrl);
        } catch (error) {
            console.error("Error uploading:", error);
            setError('Nalaganje ni uspelo. Poskusite znova.');
            setUploading(false);
        }
    };

    const saveBenefit = async (attachments, thumbnailUrl) => {
        const attachmentFields = syncAttachmentFields(attachments);
        let pinFields;

        try {
            pinFields = buildPinFields({ isPinned, pinnedUntilDate });
        } catch (pinError) {
            setError(pinError.message);
            setUploading(false);
            return;
        }

        try {
            if (isEditing && benefit) {
                await updateBenefit(benefit.id, {
                    title,
                    text,
                    unit: unit || 'general',
                    unitName,
                    attachments: attachmentFields.attachments,
                    thumbnailUrl: thumbnailUrl || benefit.thumbnailUrl || '',
                    pinnedUntil: pinFields.pinnedUntil,
                    pinPriority: pinFields.pinPriority,
                });
                setError('');
                toast.success('Ugodnost je bila posodobljena.');
                if (onBenefitUpdated) onBenefitUpdated();
            } else {
                await createBenefit(tenantId, {
                    title,
                    text,
                    unit: unit || 'general',
                    unitName,
                    uid: auth.currentUser.uid,
                    displayName: user.displayName || 'Anonimen',
                    attachments: attachmentFields.attachments,
                    thumbnailUrl: thumbnailUrl || '',
                    pinnedUntil: pinFields.pinnedUntil,
                    pinPriority: pinFields.pinPriority,
                });
                setTitle('');
                setText('');
                setUnit('general');
                setUnitName('Splošno');
                setExistingAttachments([]);
                setNewFiles([]);
                setThumbnail(null);
                setIsPinned(false);
                setPinnedUntilDate(toDateInputValue(defaultPinnedUntil()));
                setError('');
                toast.success('Ugodnost je bila objavljena.');
                onBenefitCreated();
            }
        } catch (error) {
            setError(error.message || 'Shranjevanje ugodnosti ni uspelo. Poskusite znova.');
            toast.error('Shranjevanje ugodnosti ni uspelo. Poskusite znova.');
            console.error("Error saving benefit:", error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Container className="benefit-create-container">
            {error && <Alert variant="danger">{error}</Alert>}
            <Form onSubmit={handleSubmit}>
                <Row className="mb-3">
                    <Col>
                        <Form.Group>
                            <Form.Label>Naslov</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Vpišite naslov"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <Form.Group>
                            <Form.Label>Besedilo</Form.Label>
                            <div ref={quillContainerRef} className="quill-container">
                                <ReactQuill
                                    value={text}
                                    onChange={setText}
                                    placeholder="Vpišite obvestilo"
                                    theme="snow"
                                    modules={modules}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <Form.Group>
                            <Form.Label>Enota</Form.Label>
                            <Form.Control
                                as="select"
                                value={unit}
                                onChange={handleUnitChange}
                                disabled={effectiveRole(memberships, tenantId, user) === ROLES.LOCAL_ADMIN}
                            >
                                <option value="general">Splošno</option>
                                {units.map(u => (
                                    <option key={u.id} value={u.id}>{u.unitName}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <Form.Group>
                            <Form.Check
                                type="checkbox"
                                id="benefit-pin-toggle"
                                label="Pripni na vrh seznama"
                                checked={isPinned}
                                onChange={(event) => setIsPinned(event.target.checked)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                {isPinned && (
                    <>
                        <Row className="mb-3">
                            <Col>
                                <FeaturedCarouselPinWarning
                                    visible={showOverflowWarning}
                                    module="benefits"
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Pripeto do</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={pinnedUntilDate}
                                        onChange={(event) => setPinnedUntilDate(event.target.value)}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )}
                {/* Thumbnail Upload */}
                <Row className="mb-3">
                    <Col>
                        <Form.Group>
                            <Form.Label>Ikona (thumbnail)</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailChange}
                            />
                            {isEditing && benefit.thumbnailUrl && !thumbnail && (
                                <div className="mt-2">
                                    <small>Trenutna ikona: <a href={benefit.thumbnailUrl} target="_blank" rel="noopener noreferrer">Ogled</a></small>
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <ContentAttachmentsEditor
                            label="Datoteke (neobvezno)"
                            existingAttachments={existingAttachments}
                            onExistingAttachmentsChange={setExistingAttachments}
                            newFiles={newFiles}
                            onNewFilesChange={setNewFiles}
                            hint="Dodate lahko več datotek hkrati."
                        />
                    </Col>
                </Row>
                {uploading && (
                    <ProgressBar now={uploadProgress} label={`${Math.round(uploadProgress)}%`} striped />
                )}
                <Button variant="primary" type="submit" disabled={uploading}>
                    {isEditing ? 'Posodobi ugodnost' : 'Objavi ugodnost'}
                </Button>
            </Form>
        </Container>
    );
}

BenefitCreate.defaultProps = {
    onBenefitCreated: () => {},
    onBenefitUpdated: () => {}
};

export default BenefitCreate;
